/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import lookie from 'lookie';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { useSize } from 'ahooks';
import { differenceWith, isEqual, omit } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from 'antd-mobile';
import {
  AIConsultationFrom,
  PhysicalInfoDimension,
  PhysicalInfoSource,
  TrackerCustomEvent,
  useTracker,
} from 'common-package';
import { useLocation } from 'react-router';
import { Device } from '@capacitor/device';
import { Keyboard } from '@capacitor/keyboard';
import { OrientationType, ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import useNav from '@/hooks/useNav';
import { Header, ScrollView } from '@/components';
import service from '@/service';
import { Toast } from '@/utils';

import {
  ALL_PAGES,
  CURRENT_ANDROID_ID,
  JUDGE_AS_VERTICAL_SCREEN_HEIGHT,
  SELECTED_CUSTOMER_ID,
  UPPER_STATUS_CODE,
} from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { PHYSICAL_CONDITION_ITEMS } from '@/pages/ai-consultation/components/physical-condition';
import { POSTURE_ADJUST_ITEM_2_TEXT } from '@/pages/ai-consultation/components/posture-condition';
import { selectDevicePlatform, selectedStoreInfo, setStoreInfo } from '@/store/device';
import { setCurrentCustomer } from '@/store/customer';
import ScreenFlipTip from '../../search-member/screen-flip-tip';
import { BodyInfo } from '../components/BasicInfo';
import { NameStep } from '../components/NameStep';
import { PhoneStep } from '../components/PhoneStep';
import { BirthdayStep } from '../components/BrithdayStep';
import { AIConsultationInfo, DIMENSION_TYPE_SORT } from '../components/AIConsultationInfo';
import HeaderMask from '../components/HeaderMask';
import { OtherInfoStep } from '../components/OtherInfoStep';

import cx from './index.module.scss';

interface Props {}

dayjs.extend(weekday);
dayjs.extend(localeData);
const validateMessages = {
  // eslint-disable-next-line no-template-curly-in-string
  required: '${label}不能为空',
};

const CODE_NEXT_SEND_TIME = 'add_member_code_next_send_time';
const DateFormat = 'YYYY/MM/DD';

/**
 * 通过生日计算年龄
 */
export const getAgeFromBirthday = (dateString: string): number => {
  const birthday = new Date(dateString);
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const EditMember: React.FC<Props> = () => {
  const sizeRef = useRef(null);
  const screenSize = useSize(sizeRef);
  const [isTipOpen, setIsTipOpen] = useState<boolean>(false);
  const devicePlatform = useSelector(selectDevicePlatform);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { sendEvent } = useTracker();
  const [isShowCodeField, setIsShowCodeField] = useState(true);
  const storeInfo = useSelector(selectedStoreInfo);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState<GetCustomerProfileResponse>(null);

  const nav = useNav();
  const location = useLocation();
  const userPhoneNumber = useRef(null);
  const isShowBackConfirm = useRef(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isKeyBoardShow, setIsKeyBoardShow] = useState(false);
  const currentPlatformIsAndroid = useRef(false);

  const [hopeTypesSort, setHopeTypesSort] = useState<AIDiagnose.TherapyType[]>(null);

  const isVerticalScreen = useMemo(() => screenSize?.height > JUDGE_AS_VERTICAL_SCREEN_HEIGHT, [screenSize?.height]);

  // 会员档案数据更改埋点上报
  const sendRecordEvent = useCallback(
    (fields: GetCustomerProfileResponse) => {
      const sendRecordEvent = (c1: string) =>
        sendEvent(TrackerCustomEvent.FillInPhysicalInfoSource, {
          et: 'CLK',
          c1,
          c2: PhysicalInfoSource['member-info-edit'],
        });

      // 对比数据 如果更改数据立即埋点 未更改不埋点 疼痛程度在编辑会员页面无法更改 则不埋点 删除模块也不埋点
      const diffPhysicalImproveList = differenceWith(
        currentCustomerInfo?.physicalImproveList,
        fields?.physicalImproveList,
        isEqual
      );

      const diffWeightLossList = differenceWith(currentCustomerInfo?.weightLossList, fields?.weightLossList, isEqual);

      if (diffPhysicalImproveList?.length > 0 && fields?.physicalImproveList?.length > 0) {
        sendRecordEvent(PhysicalInfoDimension['physicalLevel']);
      }

      if (diffWeightLossList?.length > 0 && fields?.weightLossList?.length > 0) {
        const shouldSendItems = diffWeightLossList?.reduce((accur, cur) => {
          accur.push(cur.problem);
          return accur;
        }, []);

        shouldSendItems.forEach(item => sendRecordEvent(PhysicalInfoDimension[item]));
      }
    },
    [currentCustomerInfo?.physicalImproveList, currentCustomerInfo?.weightLossList, sendEvent]
  );

  const handleSave = useCallback(() => {
    form.submit();
  }, [form]);

  const handleFormFinish = useCallback(
    async fields => {
      fields.birthday = fields.birthday?.format(DateFormat);
      fields.age = getAgeFromBirthday(fields.birthday);
      const bmiInfo = fields?.bmiInfo;
      delete fields.bmiInfo;

      try {
        await service.customer.updateCustomer({
          ...fields,
          ...bmiInfo,
          hopeTypes: hopeTypesSort,
          customerId: lookie.get(SELECTED_CUSTOMER_ID),
        });
        sendRecordEvent(fields);
        sendEvent?.(TrackerCustomEvent.UpdateMemberInfoClk, { et: 'CLK' });
        Toast.success('更新成功');
        nav(ALL_PAGES.MEMBER_DETAIL, { state: { isNeedShowDrawerAfterFinish: false } });
      } catch ({ code, message }) {
        if (code === UPPER_STATUS_CODE.SmsCodeError) {
          Toast.fail(message || '请输入正确的验证码');
        }
      }
    },
    [hopeTypesSort, sendRecordEvent, sendEvent, nav]
  );

  const fillFields = useCallback(async () => {
    const { state } = location;
    if (!state) return;
    const { customerId } = state || {};
    setIsShowCodeField(false);
    try {
      const { data } = await service.customer.getCustomerProfile(customerId);
      // 用户AI数据进行排序
      const newPhysicalImproveList = data?.physicalImproveList?.filter(
        item =>
          //@ts-ignore
          item.problem !== 'stayUpLate'
      );
      newPhysicalImproveList?.sort(
        (a, b) =>
          Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(a?.problem) -
          Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(b?.problem)
      );

      data?.bodyAdjustList?.sort(
        (a, b) =>
          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
      );

      setCurrentCustomerInfo({ ...data, physicalImproveList: newPhysicalImproveList });
      const {
        name,
        phoneNumber,
        birthday,
        source,
        gender,
        physicalAssessment,
        adjustmentPurpose,
        remark,
        height,
        weight,
        bodyFatRate,
        weightLossList,
        painReliefList,
        bodyAdjustList,
      } = data || {};
      userPhoneNumber.current = phoneNumber;
      const formatBirthday = dayjs(birthday, DateFormat);
      const bmiInfo = { height, weight, bodyFatRate };
      // 对减肥瘦身数据进行排序
      weightLossList?.sort(
        (a, b) => DIMENSION_TYPE_SORT?.indexOf(a?.problem) - DIMENSION_TYPE_SORT?.indexOf(b?.problem)
      ) || [];

      form.setFieldsValue({
        name,
        phoneNumber,
        source,
        gender,
        physicalAssessment,
        adjustmentPurpose,
        remark,
        bmiInfo,
        weightLossList,
        painReliefList,
        physicalImproveList: newPhysicalImproveList,
        bodyAdjustList,
        ...(birthday ? { birthday: formatBirthday } : {}),
      });
    } finally {
      scrollViewRef.current?.bsInstance?.scrollTo(0, 0);
      setTimeout(() => scrollViewRef.current?.handleRefresh(), 500);
    }
  }, [form, location]);

  const backConfirm = useCallback(() => {
    if (isShowBackConfirm.current) return;
    isShowBackConfirm.current = true;
    const { state } = location;
    Dialog.confirm({
      destroyOnClose: true,
      title: `是否要放弃编辑会员？`,
      content: '退出后已填写的内容将不会保留',
      confirmText: '确认',
      onConfirm: () => {
        isShowBackConfirm.current = false;
        state?.isSearchMember
          ? nav(ALL_PAGES.SEARCH_MEMBER)
          : nav(ALL_PAGES.MEMBER_DETAIL, { state: { isNeedShowDrawerAfterFinish: false } });
      },
      onCancel: () => {
        isShowBackConfirm.current = false;
      },
    });
  }, [location, nav]);

  const initListener = useCallback(async () => {
    const { platform } = await Device.getInfo();

    if (platform === 'android') {
      currentPlatformIsAndroid.current = true;
      Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyBoardShow(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyBoardShow(false);
      });
    }
  }, []);

  const handleNavAiConsultation = useCallback(() => {
    dispatch(
      setCurrentCustomer(
        omit({ ...currentCustomerInfo, isVerticalScreen, isTipOpen, setIsTipOpen }, ['isTipOpen', 'setIsTipOpen'])
      )
    );
    lookie.set(SELECTED_CUSTOMER_ID, currentCustomerInfo?.customerId);

    sendEvent?.(TrackerCustomEvent.AiConsultationClk, {
      et: 'CLK',
      c1: AIConsultationFrom.MemberEdit,
    });

    if (devicePlatform === 'android') {
      setIsTipOpen(true);
      setTimeout(() => ScreenOrientation.lock({ type: OrientationType.PORTRAIT_PRIMARY }), 0);
    } else {
      nav(ALL_PAGES.AI_CONSULTATION, {
        state: {
          action: 'member-detail',
        },
      });
    }
  }, [currentCustomerInfo, devicePlatform, dispatch, isTipOpen, isVerticalScreen, nav, sendEvent]);

  // 更新字段值
  const handleUpdateFormProblems = useCallback(() => {
    setIsUpdate(true);
    scrollViewRef.current?.handleRefresh();
  }, []);

  const getBoundStoreInfo = async () => {
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    try {
      const { data } = await service.system.getBoundStore(androidId);
      dispatch(setStoreInfo(data));
    } catch (e) {}
  };

  useEffect(() => {
    initListener();
    fillFields();

    return () => {
      lookie.remove(CODE_NEXT_SEND_TIME);
      currentPlatformIsAndroid.current && Keyboard.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isTipOpen && isVerticalScreen && devicePlatform === 'android') {
      nav(ALL_PAGES.AI_CONSULTATION, {
        state: {
          action: 'member-detail',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerticalScreen, isTipOpen, devicePlatform]);

  useEffect(() => {
    getBoundStoreInfo();
  }, []);

  return (
    <div className={cx('add-member')} ref={sizeRef}>
      <Header
        title={'编辑会员'}
        backConfig={{ text: location.state?.isSearchMember ? '搜索会员' : '返回详情', action: backConfirm }}
      />
      <div className={cx('bg-mirror')} />

      <div className={cx('wrapper')}>
        <div className={cx('top-bar')}>
          <span />
          <span
            className={cx('selected', { 'is-update': !isUpdate })}
            onClick={() => {
              isUpdate && handleSave();
            }}
          >
            更新
          </span>
        </div>
        <ScrollView ref={scrollViewRef} scrollY className={cx('scroll-box')} scrollbar={{ fade: false }}>
          <div className={cx('form-content')}>
            <Form
              form={form}
              onFieldsChange={changedFields => {
                setIsUpdate(true);
                if (changedFields.some(field => field.name[0] === 'phoneNumber')) {
                  const newNumber = changedFields.find(field => field.name[0] === 'phoneNumber')?.value;
                  setIsShowCodeField(newNumber !== userPhoneNumber.current);
                  newNumber !== userPhoneNumber.current && scrollViewRef.current?.handleRefresh(); // 显示验证码后 需要刷新滑动区域高度
                }
                // NOTE: 如果会员选择其他信息DOM发生变化，需要重新刷新
                if (
                  changedFields.some(
                    field => field.name[0] === 'physicalAssessment' || field.name[0] === 'adjustmentPurpose'
                  )
                ) {
                  scrollViewRef.current?.handleRefresh();
                }
              }}
              className={cx('form')}
              onFinish={handleFormFinish}
              validateMessages={validateMessages}
            >
              <NameStep form={form} hidden={false} />

              <BodyInfo form={form} hidden={false} />

              {currentCustomerInfo?.hopeTypes?.length > 0 && (
                <AIConsultationInfo
                  form={form}
                  customerInfo={currentCustomerInfo}
                  handleUpdateFormProblems={handleUpdateFormProblems}
                  setHopeTypesSort={setHopeTypesSort}
                  hopeTypesSort={hopeTypesSort}
                  handleNavAiConsultation={handleNavAiConsultation}
                />
              )}

              <OtherInfoStep form={form} hidden={false} hasHopeType={currentCustomerInfo?.hopeTypes?.length > 0} />

              <PhoneStep
                form={form}
                hidden={false}
                isValidPhone={storeInfo?.phoneNumberCheck === 1}
                isShowCode={storeInfo?.phoneNumberCheck === 1 && isShowCodeField}
                currentIsEdit
              />

              <BirthdayStep form={form} hidden={false} className={cx('birthday-step')} />
            </Form>
          </div>
        </ScrollView>
      </div>
      <ScreenFlipTip open={isTipOpen} setOpen={setIsTipOpen} />

      <HeaderMask show={isKeyBoardShow} />
    </div>
  );
};

export default EditMember;
